import { useReviewerSchema } from "../../../server/auth/use-reviewer-schema";
import { useSupabaseLoader } from "../../../server/supabase/hooks";

import type { SupabaseHandlerArg } from "../../../server/supabase/hooks/types";

/**
 * How many Communications await the signed-in user's approval: those the Approvals list shows as
 * awaiting approval. The database counts them (`pending_review_count`), so the list isn't sent.
 */
export const usePendingReviewCount = (
  enabled = true,
  refreshKey?: unknown,
): { data: number | null; loading: boolean; error: Error | null } => {
  // A reviewer is granted none of the tables the count is drawn from, so they come in through the
  // reviewer schema instead, as for the list.
  const reviewerSchema = useReviewerSchema();

  const { data, loading, error } = useSupabaseLoader<number, "public" | "reviewer">(
    async ({ supabase, account_id }) => {
      if (!enabled) return 0;

      const { data: count, error: countError } = reviewerSchema
        ? // oxlint-disable-next-line typescript/no-unsafe-type-assertion
          await (supabase as SupabaseHandlerArg<"reviewer">["supabase"]).rpc(
            "pending_review_count",
            { account_id_: account_id },
          )
        : // oxlint-disable-next-line typescript/no-unsafe-type-assertion
          await (supabase as SupabaseHandlerArg<"public">["supabase"]).rpc("pending_review_count", {
            account_id_: account_id,
          });

      if (countError) throw countError;

      return count;
    },
    [reviewerSchema, enabled, refreshKey],
    reviewerSchema ? "reviewer" : "public",
  );

  return { data, loading, error };
};
